import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { useState } from "react";

import { StyledAnchor } from "@/components/common/styles";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import { navClearance } from "@/components/marketing/header/tokens";
import HeroVideoDialog from "./HeroVideoDialog";
import { ActionButton, SectionContainer } from "./HomePrimitives";
import { HOME_HERO, type HeroTile } from "./homeContent";
import { BODY } from "./homeType";

/** Four across, then two, then one, the tile grid's whole responsive story. */
const TILE_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "12px",
  [NOT_WIDE]: { gridTemplateColumns: "repeat(2, 1fr)" },
  [TINY]: { gridTemplateColumns: "1fr", gap: "8px" },
};

/**
 * Below 480 the tile stops being a card with a picture and becomes a row with a
 * glyph: the illustration is dropped and the 32px icon takes its place. Two
 * images per tile is what the published markup ships, and swapping them by
 * media query is what its stylesheet does.
 */
function Tile({ tile }: { tile: HeroTile }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        p: "16px",
        borderRadius: "8px",
        backgroundColor: "background.default",
        [TINY]: {
          flexDirection: "row",
          alignItems: "center",
          gap: "12px",
          p: "12px",
        },
      }}
    >
      <Box
        component="img"
        src={tile.icon}
        alt=""
        loading="lazy"
        sx={{
          display: "none",
          [TINY]: { display: "block", width: 32, height: 32, flex: "0 0 auto" },
        }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Box
          sx={{
            color: marketingPalette.ink,
            fontSize: 19,
            lineHeight: "28.5px",
            WebkitTextStrokeWidth: "0.7px",
            [TINY]: { fontSize: 16, lineHeight: "20px" },
          }}
        >
          {tile.title}
        </Box>
        <Box sx={{ fontSize: 14, lineHeight: "136%", opacity: 0.85 }}>
          {tile.subtitle}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 1,
          [TINY]: { display: "none" },
        }}
      >
        <Box
          component="img"
          src={tile.image}
          alt=""
          loading="lazy"
          sx={{ maxWidth: 1 }}
        />
      </Box>
    </Box>
  );
}

export default function HeroSection({
  clearance,
  heroVideo,
}: {
  clearance: Parameters<typeof navClearance>[0];
  /** The CMS's video, or null while there is none. */
  heroVideo: string | null;
}) {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    // `top` would state the published padding again on top of the bar this page
    // already cleared in flow; `navClearance` subtracts the bar from it.
    <SectionContainer padding="plain" sx={navClearance(clearance)}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          p: "32px",
          borderRadius: "32px",
          backgroundColor: marketingPalette.heroPanel,
          [NOT_WIDE]: { p: "16px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12.8px",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12.8px",
              width: 1,
              maxWidth: "69ch",
            }}
          >
            <Typography variant="h1" component="h1">
              {HOME_HERO.heading}
            </Typography>
            <Typography component="p" sx={BODY}>
              {HOME_HERO.body}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                columnGap: "19.2px",
                WebkitTextStrokeWidth: "0.5px",
                [TINY]: {
                  flexWrap: "wrap",
                  justifyContent: "center",
                  rowGap: "4.8px",
                  width: 1,
                },
              }}
            >
              {HOME_HERO.chips.map((chip) => (
                <Box
                  key={chip.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6.4px",
                    fontSize: 18,
                    lineHeight: "27px",
                    [TINY]: { fontSize: 16, lineHeight: "24px" },
                  }}
                >
                  <Box
                    component="img"
                    src={chip.icon}
                    alt=""
                    loading="lazy"
                    sx={{ width: 21, height: 21 }}
                  />
                  {chip.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              width: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                width: 1,
                [MID]: { flexDirection: "column", alignItems: "stretch" },
                [TINY]: { gap: "12px" },
              }}
            >
              <ActionButton cta={HOME_HERO.primaryCta} width="hero" />
              <StyledAnchor
                href={HOME_HERO.googleCta.href}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  flex: "0 0 auto",
                  height: "48px",
                  width: "204px",
                  px: "22.4px",
                  borderRadius: "8px",
                  backgroundColor: marketingPalette.surface,
                  border: "1px solid rgba(79, 92, 101, 0.6)",
                  color: marketingPalette.ink,
                  transition:
                    "padding-left 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
                  "&:hover": {
                    backgroundColor: marketingPalette.ink,
                    color: "common.white",
                  },
                  [MID]: { width: 1 },
                }}
              >
                {/*
                  The published button hosts a Discount Animated Icon Lottie
                  pinned to its left edge, and rests it at `opacity: 0` — it is
                  invisible at every state measured, and drawn it covers the
                  Google mark. Left out rather than invented; the asset is
                  vendored for whoever decides what should reveal it.
                */}
                <Box
                  component="img"
                  src={HOME_HERO.googleLogo}
                  alt=""
                  loading="lazy"
                  sx={{ width: 20, height: 20 }}
                />
                <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                  {HOME_HERO.googleCta.label}
                </Box>
              </StyledAnchor>
            </Box>
            {/*
              A control, not a link: the published trigger is a bare div with a
              `data-modal_1-trigger` attribute, so ported as an anchor it would
              be a dead jump to the top of the page.

              Drawn only while the CMS holds a video. The published page hides
              the whole control while its own source is gone, and a control that
              opens a modal with nothing to play is the same thing as broken.
              It comes back on the next build once a file is uploaded.
            */}
            {heroVideo === null ? null : (
              <ButtonBase
                type="button"
                onClick={() => setVideoOpen(true)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  p: "6.08px 12px",
                  borderRadius: "8px",
                  fontFamily: "inherit",
                  fontSize: 15,
                  // `ButtonBase` sets its own leading; the published control keeps the body's.
                  lineHeight: 1.5,
                  color: marketingPalette.ink,
                  backgroundColor: marketingPalette.bannerTint,
                  WebkitTextStrokeWidth: "0.5px",
                  "&:hover": {
                    color: "#008266",
                    backgroundColor: "rgba(0, 142, 114, 0.16)",
                  },
                }}
              >
                {HOME_HERO.videoLabel}
                <Box
                  component="img"
                  src={HOME_HERO.playIcon}
                  alt=""
                  loading="lazy"
                  sx={{ width: 16, height: 16 }}
                />
              </ButtonBase>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            [TINY]: { gap: "8px" },
          }}
        >
          <Box sx={TILE_GRID}>
            {HOME_HERO.tiles.slice(0, 4).map((tile) => (
              <Tile key={tile.title} tile={tile} />
            ))}
          </Box>
          <Box sx={TILE_GRID}>
            {HOME_HERO.tiles.slice(4).map((tile, index) => (
              <Box
                key={tile.title}
                sx={
                  // The last tile fills the row's spare track, and gives it back
                  // once there is only one column to fill.
                  index === 2
                    ? { gridColumn: "span 2", [TINY]: { gridColumn: "span 1" } }
                    : undefined
                }
              >
                <Tile tile={tile} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {heroVideo === null ? null : (
        <HeroVideoDialog
          open={videoOpen}
          onClose={() => setVideoOpen(false)}
          src={heroVideo}
        />
      )}
    </SectionContainer>
  );
}
